import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCita } from '@providers/citaContext';
import Titulo from '@components/Titulo';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import Consultar from '@components/Consultar';

import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { validateBoldOrderApi } from '@api/wp/agenda/confirmar/confirmarApi';
import { colors } from '@styles/colors';
import { MEMBERSHIP_URL } from '@utils/const';

/** Copia de cita en sessionStorage para sobrevivir F5 tras clearCita() */
const SNAPSHOT_CITA_FINAL = 'previsalud_final_cita_snapshot';

function citaVacia(fuente) {
  if (fuente == null) return true;
  if (typeof fuente !== 'object') return true;
  const keys = Object.keys(fuente).filter((k) => k !== 'fromVentanillaExito');
  return keys.length === 0;
}

function leerCitaDesdeSessionStorage(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return citaVacia(p) ? null : p;
  } catch (_) {
    return null;
  }
}

const Final = () => {
  const [cargando, setCargando] = useState(false);
  const [datosCompletos, setDatosCompletos] = useState(null);
  /** Validación contra oficina_bold_response (retorno desde checkout Bold). */
  const [validandoBoldApi, setValidandoBoldApi] = useState(false);
  /** Segundos transcurridos mientras se valida el pago con el servidor. */
  const [segundosValidando, setSegundosValidando] = useState(0);
  /** Tras validar pago Bold OK: mostrar mensaje de éxito (sin redirección automática). */
  const [boldPagoAprobado, setBoldPagoAprobado] = useState(false);
  const [boldError, setBoldError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const nombreCompleto = (localStorage.getItem('nombreCompleto') || '').toString();
  const { cita, clearCita } = useCita();
  const scriptBoldInyectadoRef = useRef(false);

  /** bold-order-id en la URL = regreso desde pago Bold (no ventanilla). */
  const { boldOrderId, esRetornoBoldCheckout } = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const raw = p.get('bold-order-id');
    const orderId = raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
    return {
      boldOrderId: orderId,
      esRetornoBoldCheckout: Boolean(orderId),
    };
  }, [location.search]);

  const base = datosCompletos || cita || {};
  const {
    ciudadId,
    ciudadNombre,
    especialidadNombre,
    oidEspecialidad,
    empresaNombre,
    sedeId,
    sedeNombre,
    fecha,
    medicoNombre,
    horaSeleccionada,
    valorPreviplan,
    sku,
    nombreServicio,
    metodo,
  } = base;

  const especialidadResumen = especialidadNombre || nombreServicio || '';
  const horaCitaTexto =
    typeof horaSeleccionada === 'string'
      ? horaSeleccionada
      : horaSeleccionada && typeof horaSeleccionada === 'object' && horaSeleccionada.hora != null
        ? String(horaSeleccionada.hora)
        : '';
  const medicoResumen = medicoNombre || '';

  const formatFechaFinal = (s) => {
    if (!s) return '';
    const parts = String(s).split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d}/${m}/${y}`;
    }
    return String(s);
  };

  // Validación: si no hay sesión en localStorage o es "0", redirigir a membresía
  useEffect(() => {
    const idUsu = localStorage.getItem('idUsu');
    if (!idUsu || idUsu === '0') {
      window.location.href = MEMBERSHIP_URL;
    }
  }, []);

  const irAUbicacion = () => {
    try {
      sessionStorage.removeItem(SNAPSHOT_CITA_FINAL);
    } catch (_) {
      /* noop */
    }
    navigate('/home', {
      state: {
        ciudadId,
        ciudadNombre,
        especialidadNombre,
        nombreServicio,
        empresaNombre,
        oidEspecialidad,
        valorPreviplan,
        sku,
      },
    });
  };

  // Primera carga: datos desde contexto, navegación, Bold temp o snapshot; persistir snapshot y limpiar contexto de cita.
  useEffect(() => {
    let fuente = null;
    if (cita && !citaVacia(cita)) fuente = cita;
    else if (location.state && !citaVacia(location.state)) fuente = location.state;
    else if (esRetornoBoldCheckout) {
      fuente = leerCitaDesdeSessionStorage('previsalud_final_cita_bold');
    }
    if (!fuente) {
      fuente = leerCitaDesdeSessionStorage(SNAPSHOT_CITA_FINAL);
    }

    if (fuente && !citaVacia(fuente)) {
      setDatosCompletos(fuente);
      try {
        sessionStorage.setItem(SNAPSHOT_CITA_FINAL, JSON.stringify(fuente));
      } catch (_) {
        /* noop */
      }
      try {
        clearCita();
      } catch (_) {
        /* noop */
      }
    }

    if (!scriptBoldInyectadoRef.current) {
      scriptBoldInyectadoRef.current = true;
      const script = document.createElement('script');
      script.src = 'https://checkout.bold.co/library/boldPaymentButton.js';
      script.async = true;
      document.head.appendChild(script);
      return () => {
        scriptBoldInyectadoRef.current = false;
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
    return undefined;
  }, [cita, location.state, esRetornoBoldCheckout, clearCita]);

  // F5 u otra recarga sin contexto: si no hay datos recuperables → Ciudad (/home)
  useEffect(() => {
    if (datosCompletos != null && !citaVacia(datosCompletos)) {
      return undefined;
    }

    const tid = window.setTimeout(() => {
      let fuente = leerCitaDesdeSessionStorage(SNAPSHOT_CITA_FINAL);
      if (!fuente && esRetornoBoldCheckout) {
        fuente = leerCitaDesdeSessionStorage('previsalud_final_cita_bold');
      }
      if (fuente && !citaVacia(fuente)) {
        setDatosCompletos(fuente);
        try {
          sessionStorage.setItem(SNAPSHOT_CITA_FINAL, JSON.stringify(fuente));
        } catch (_) {
          /* noop */
        }
        return;
      }
      navigate('/home', { replace: true });
    }, 220);

    return () => window.clearTimeout(tid);
  }, [datosCompletos, esRetornoBoldCheckout, navigate]);

  // Contador mientras corre la validación del pago (Bold → backend)
  useEffect(() => {
    if (!validandoBoldApi) {
      setSegundosValidando(0);
      return undefined;
    }
    setSegundosValidando(0);
    const id = setInterval(() => {
      setSegundosValidando((s) => s + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [validandoBoldApi]);

  // Retorno Bold: consultar oficina_bold_response (order_id + type SALE_APPROVED), con reintentos por latencia del webhook
  useEffect(() => {
    if (!esRetornoBoldCheckout || !boldOrderId) return undefined;

    let cancelado = false;
    setValidandoBoldApi(true);
    setCargando(true);
    setBoldError(null);
    setBoldPagoAprobado(false);

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const maxEsperaMs = 60000;
    const intervaloMs = 2000;

    (async () => {
      let ultimoError = null;
      const inicio = Date.now();

      while (!cancelado && Date.now() - inicio < maxEsperaMs) {
        try {
          const resp = await validateBoldOrderApi({ order_id: boldOrderId });
          if (cancelado) return;
          const r = resp && resp.result;
          if (resp && resp.statusCode === 200 && r && r.approved) {
            try {
              sessionStorage.removeItem('previsalud_final_cita_bold');
            } catch (_) {
              /* noop */
            }
            setValidandoBoldApi(false);
            setBoldPagoAprobado(true);
            setCargando(false);
            return;
          }
        } catch (err) {
          if (cancelado) return;
          ultimoError = err;
          console.error('Error validando pago Bold (oficina_bold_response):', err);
        }
        await sleep(intervaloMs);
      }

      if (!cancelado) {
        const msg =
          ultimoError && ultimoError.message
            ? ultimoError.message
            : 'No se encontró el pago como aprobado (SALE_APPROVED). Si ya pagó, espere un momento o contacte a soporte.';
        setBoldError(msg);
        setValidandoBoldApi(false);
        setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [esRetornoBoldCheckout, boldOrderId]);

  const subtituloTitulo = useMemo(() => {
    if (!esRetornoBoldCheckout) return 'Resumen de la cita';
    if (boldError) return 'Pago Bold';
    if (boldPagoAprobado && !validandoBoldApi) return 'Pago aprobado';
    return 'Confirmando pago Bold';
  }, [esRetornoBoldCheckout, boldError, boldPagoAprobado, validandoBoldApi]);

  const boldEsperandoConfirmacion =
    esRetornoBoldCheckout && validandoBoldApi && !boldError && !boldPagoAprobado;
  const boldListoRedirect = esRetornoBoldCheckout && boldPagoAprobado && !validandoBoldApi;

  const avisoBannerSobreCajita = boldEsperandoConfirmacion;

  /** Esquina superior derecha de la caja de datos de la cita. */
  const etiquetaEstadoPagoCaja = useMemo(() => {
    if (esRetornoBoldCheckout && boldError) return null;
    if (esRetornoBoldCheckout && boldPagoAprobado) return null;
    if (esRetornoBoldCheckout) return 'PAGO EN PROCESO';
    const m = String(metodo ?? '').trim().toLowerCase();
    if (m === 'bold') return 'PAGO EN PROCESO';
    if (m === 'ventanilla' || Boolean(location.state?.fromVentanillaExito)) return 'PAGO PENDIENTE';
    return null;
  }, [esRetornoBoldCheckout, boldError, boldPagoAprobado, metodo, location.state]);

  /** Misma banda horizontal que #cajaTitulo en Titulo (marginLeft 6vh, ancho 90vh). */
  const anchoBandaTitulo = 'min(90vh, calc(100% - 6vh))';
  const anchoCajaResumenCita = 'min(73.8vh, 100%)';

  return (
    <div id="cajaPrincipal" style={cajaPrincipalStyle}>
      <Titulo
        titulo="Resumen de la cita"
        subtitulo={subtituloTitulo}
        rutaRetorno={esRetornoBoldCheckout ? '/home' : '/agenda/horas'}
      />

      {esRetornoBoldCheckout && boldError && (
        <div
          style={{
            margin: '0 auto',
            maxWidth: '90vh',
            padding: '1.4vh 2vh',
            textAlign: 'center',
            fontFamily: 'Inter',
            fontSize: '2vh',
            fontWeight: 600,
            color: colors.primaryText,
            backgroundColor: colors.errorBg,
            border: `2px solid ${colors.error}`,
            borderRadius: '1.2vh',
            boxSizing: 'border-box',
          }}
        >
          <p style={{ margin: '0 0 0.8vh', fontWeight: 700 }}>No se pudo validar el pago</p>
          <p style={{ margin: '0 0 1vh', fontSize: '1.85vh', fontWeight: 500 }}>{boldError}</p>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(SNAPSHOT_CITA_FINAL);
              } catch (_) {
                /* noop */
              }
              navigate('/home', { replace: true });
            }}
            style={{
              padding: '1vh 2vh',
              borderRadius: '1.5vh',
              border: 'none',
              backgroundColor: colors.accent,
              color: colors.white,
              fontFamily: 'Inter',
              fontSize: '1.85vh',
              cursor: 'pointer',
            }}
          >
            Volver al inicio de la agenda
          </button>
        </div>
      )}

      <div
        style={{
          alignSelf: 'stretch',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            marginLeft: '6vh',
            marginTop: '0.5vh',
            marginBottom: '2vh',
            paddingTop: '0.35vh',
            width: anchoBandaTitulo,
            maxWidth: '90vh',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            boxSizing: 'border-box',
          }}
        >
          {esRetornoBoldCheckout && boldEsperandoConfirmacion && (
            <div
              id="cajaFinalBoldEsperandoConfirmacion"
              style={{
                width: anchoCajaResumenCita,
                maxWidth: '100%',
                margin: '0 auto 1.2vh auto',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  padding: '1.4vh 2vh',
                  textAlign: 'center',
                  fontFamily: 'Inter',
                  fontSize: '2vh',
                  fontWeight: 600,
                  color: colors.primaryText,
                  backgroundColor: colors.gray50,
                  border: `2px solid ${colors.primary}`,
                  borderRadius: '1.2vh',
                  boxSizing: 'border-box',
                }}
              >
                Verificando su pago Bold en el sistema… Tiempo transcurrido:{' '}
                <strong style={{ color: colors.accent }}>{segundosValidando}</strong> s
                {cargando ? (
                  <span style={{ display: 'block', marginTop: '0.6vh', fontSize: '1.75vh', fontWeight: 500 }}>
                    Conectando con el servidor…
                  </span>
                ) : null}
                <span
                  style={{
                    display: 'block',
                    marginTop: '1vh',
                    fontSize: '1.85vh',
                    fontWeight: 600,
                    color: colors.primaryText,
                  }}
                >
                  Podrá solicitar otra cita o volver al inicio cuando el pago quede confirmado.
                </span>
              </div>
            </div>
          )}

          <div
            id="cajaResumen"
            style={{
              position: 'relative',
              width: anchoCajaResumenCita,
              maxWidth: '100%',
              minHeight: '50vh',
              height: 'auto',
              borderStyle: 'solid',
              borderColor: '#29D9C5',
              marginTop: avisoBannerSobreCajita ? '0.6vh' : '1vh',
              borderWidth: '1vh',
              borderRadius: '2vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              padding: '2vh 1.5vh 2.5vh',
            }}
          >
            {etiquetaEstadoPagoCaja ? (
              <span
                style={{
                  position: 'absolute',
                  top: '1.4vh',
                  right: '1.4vh',
                  zIndex: 2,
                  maxWidth: '46%',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1.35vh',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  lineHeight: 1.2,
                  textAlign: 'right',
                  boxSizing: 'border-box',
                }}
              >
                {etiquetaEstadoPagoCaja}
              </span>
            ) : null}
            <div
              id="cajaResumenContenido"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {boldListoRedirect ? (
                <style>
                  {`
                    @keyframes finalBoldPulgarExito {
                      0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 161, 155, 0.5); }
                      35% { transform: scale(1.12); box-shadow: 0 0 2.2vh rgba(41, 217, 197, 0.65); }
                      65% { transform: scale(1.04); box-shadow: 0 0 1.4vh rgba(0, 161, 155, 0.45); }
                      100% { transform: scale(1); box-shadow: 0 0 1vh rgba(0, 161, 155, 0.35); }
                    }
                  `}
                </style>
              ) : null}
              <div
                style={{
                  width: '10.461vh',
                  height: '10.461vh',
                  backgroundColor: '#00A19B',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.6vh',
                  flexShrink: 0,
                  ...(boldListoRedirect
                    ? {
                        animation: 'finalBoldPulgarExito 1.15s ease-in-out 4',
                        animationFillMode: 'forwards',
                        willChange: 'transform, box-shadow',
                      }
                    : {}),
                }}
              >
                <ThumbUpAltIcon style={{ color: '#FFFFFF', fontSize: '6vh' }} />
              </div>

              <span
                style={{
                  fontSize: '2.5vh',
                  fontWeight: '600',
                  fontFamily: 'Inter',
                  color: '#00A19B',
                  textAlign: 'center',
                }}
              >
                {boldListoRedirect
                  ? '¡Pago aprobado!'
                  : boldEsperandoConfirmacion
                    ? 'Confirmando su pago'
                    : '¡Hemos terminado!'}
              </span>
              <span
                style={{
                  fontSize: '3vh',
                  fontWeight: '600',
                  fontFamily: 'Inter',
                  textAlign: 'center',
                  marginTop: '0.6vh',
                }}
              >
                {nombreCompleto || 'Nombre no disponible'}
              </span>

              <div
                style={{
                  marginTop: '1.6vh',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    fontSize: '2.1vh',
                    fontWeight: 600,
                    fontFamily: 'Inter',
                    color: '#00A19B',
                    letterSpacing: '0.02em',
                  }}
                >
                  Tu Cita
                </div>
                {especialidadResumen ? (
                  <div
                    style={{
                      marginTop: '0.45vh',
                      fontSize: '2.35vh',
                      fontWeight: 700,
                      fontFamily: 'Inter',
                      color: '#333',
                      lineHeight: 1.3,
                    }}
                  >
                    {especialidadResumen}
                  </div>
                ) : null}
              </div>

              <div style={{ height: '1.6vh', width: '100%', flexShrink: 0 }} aria-hidden />

              <span
                style={{
                  fontSize: '2vh',
                  fontFamily: 'Inter',
                  marginLeft: '1vh',
                  marginRight: '1vh',
                  textAlign: 'center',
                  color: '#333',
                  lineHeight: 1.45,
                }}
              >
                {boldListoRedirect
                  ? 'Su cita quedó confirmada. Puede solicitar otra cita o volver al inicio de la agenda cuando lo desee.'
                  : boldEsperandoConfirmacion
                    ? 'Su cita está registrada. Estamos comprobando en el sistema que el pago con Bold quedó aprobado; puede tardar unos segundos.'
                    : 'Ha sido agenda exitosamente, pero debes realizar el pago por ventanilla en el punto de atención.'}
              </span>

              <div style={{ height: '2vh', width: '100%', flexShrink: 0 }} aria-hidden />

              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  fontSize: '1.65vh',
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  color: '#333',
                  lineHeight: 1.12,
                }}
              >
                {(fecha || horaCitaTexto) && (
                  <div style={{ lineHeight: 1.1 }}>
                    <strong>Fecha y Hora:</strong>{' '}
                    {fecha ? formatFechaFinal(fecha) : ''}
                    {fecha && horaCitaTexto ? ' · ' : ''}
                    {horaCitaTexto || ''}
                  </div>
                )}
                {medicoResumen ? (
                  <div style={{ marginTop: '0.15vh', lineHeight: 1.1 }}>
                    <strong>Profesional:</strong> {medicoResumen}
                  </div>
                ) : null}
                {sedeNombre ? (
                  <div style={{ marginTop: '0.15vh', lineHeight: 1.1 }}>
                    <strong>Sede:</strong> {sedeNombre}
                  </div>
                ) : null}
              </div>

              <div style={{ height: '2vh', width: '100%', flexShrink: 0 }} aria-hidden />

              <div
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  textAlign: 'center',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  boxSizing: 'border-box',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    whiteSpace: 'nowrap',
                    fontSize: '1.5vh',
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    color: '#333',
                    lineHeight: 1.22,
                    paddingLeft: '0.25vh',
                    paddingRight: '0.25vh',
                  }}
                >
                  {'Recuerda presentarte 30 min antes y realizar el pago por\u00A0ventanilla'}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2vh', width: '100%' }}>
            <Consultar
              label="Solicitar otra cita"
              onClick={irAUbicacion}
              ancho="40vh"
              alto="7vh"
              disabled={cargando}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Final;
